/**
 * Dimension policy: the single place that decides, per object dimension,
 * whether to SNAP a scanned (RoomPlan) measurement to a real-world
 * standard or to HONOR the measurement as-is.
 *
 * Policy (agreed 2026-06-19 — see
 * claude-docs/2026-06-19-faithful-scanned-dimensions.md):
 *   Keep snapping to a standard within a tolerance band, and only use the
 *   scanned dimension when it is BOTH reasonable AND significantly
 *   different from every standard (e.g. a genuinely large 60" range).
 *
 * `snapOrHonor` is the shared mechanism; `*_DIMENSION_POLICY` are the
 * grouped, tunable specs. To change behavior, edit the spec values here —
 * callers don't carry their own thresholds.
 *
 * Specs reference existing canonical constants (cabinet/upper-cabinet
 * dims) rather than re-hardcoding them, so there's one source of truth per
 * standard. The tolerance/min/max bands and the appliance standard sets
 * (which were previously post-processing locals) live here.
 */

const {
  INCHES_TO_METERS,
  KITCHEN_CABINET_DIMENSIONS,
  UPPER_CABINET_DIMENSIONS,
} = require('../kitchen-assembly/constants');

const IN = INCHES_TO_METERS;

// Float slack so a difference that is exactly `tolerance` (e.g. 38" is
// exactly 2" from a 36" standard) snaps rather than tipping into "honor"
// due to binary-float rounding.
const SNAP_EPS = 1e-9;

/**
 * Nearest value in `options` to `current` (absolute distance). Pure.
 * @param {number} current
 * @param {number[]} options - non-empty list of candidate standards
 * @returns {number}
 */
function nearestStandard(current, options) {
  let best = options[0];
  let bestDelta = Math.abs(current - best);
  for (const v of options) {
    const d = Math.abs(current - v);
    if (d < bestDelta) {
      best = v;
      bestDelta = d;
    }
  }
  return best;
}

/**
 * Decide the dimension to use for a scanned measurement, per the
 * snap-vs-honor policy.
 *
 * @param {number|null|undefined} scanned - the measured dimension (meters)
 * @param {Object} spec
 * @param {number[]} spec.standards - real-world standard sizes (meters)
 * @param {number} spec.tolerance - snap band; |scanned - nearest| ≤ this → snap.
 *   Set to `Infinity` to disable honoring entirely (always snap to the nearest
 *   standard) — this is how a dimension keeps the old always-snap behavior
 *   until we deliberately give it a finite tolerance.
 * @param {number} spec.min - lower bound of the "reasonable" honor range (m)
 * @param {number} spec.max - upper bound of the "reasonable" honor range (m)
 * @param {number} spec.fallback - default standard when scan is missing/garbage
 * @returns {{ value: number, action: 'snap'|'honor'|'fallback', nearest: number }}
 */
function snapOrHonor(scanned, spec) {
  const { standards, tolerance, min, max, fallback } = spec;

  // No usable measurement → fall back to the default standard.
  if (scanned == null || !Number.isFinite(scanned) || scanned <= 0) {
    return { value: fallback, action: 'fallback', nearest: fallback };
  }

  const nearest = nearestStandard(scanned, standards);

  // Close to a real standard → treat the gap as scan noise and snap.
  // (tolerance: Infinity → this branch always wins → always snap.)
  if (Math.abs(scanned - nearest) <= tolerance + SNAP_EPS) {
    return { value: nearest, action: 'snap', nearest };
  }

  // Significantly off but physically plausible → honor the measurement.
  if (scanned >= min && scanned <= max) {
    return { value: scanned, action: 'honor', nearest };
  }

  // Significantly off and implausible → garbage; fall back to default.
  return { value: fallback, action: 'fallback', nearest };
}

/**
 * Convenience wrapper: look the spec up by key and apply `snapOrHonor`.
 * @param {number|null|undefined} scanned
 * @param {string} key - a key in KITCHEN_DIMENSION_POLICY
 * @returns {{ value: number, action: string, nearest: number }}
 */
function applyKitchenDimensionPolicy(scanned, key) {
  const spec = KITCHEN_DIMENSION_POLICY[key];
  if (!spec) throw new Error(`Unknown kitchen dimension policy key: ${key}`);
  return snapOrHonor(scanned, spec.scan);
}

// ─── Kitchen policy table ────────────────────────────────────────────────────
// Tunable in one place. Each entry has TWO independent lists:
//
//   scan:   how to interpret a SCANNED dimension (post-processing, Path A).
//           `standards` are snap targets; a scanned value within `tolerance`
//           of one snaps to it; outside tolerance but inside [min, max] is
//           honored as-is; everything else falls back. `tolerance: Infinity`
//           = always snap (no honoring).
//   design: recommended sizes for LAYOUT GENERATION (Path B / APPLIANCE_
//           FOOTPRINTS). `standard` is the default tier; `large` the large
//           tier. These are what we PROPOSE in a redesign, independent of the
//           (wider) set of real sizes a scan might land on.
//
// The two lists are deliberately separate: the scan side may recognize many
// real-world sizes (so we snap a scan faithfully), while the design side
// offers a small curated set.

const KITCHEN_DIMENSION_POLICY = {
  // Range / stove width — always snap a scan to the nearest real range width.
  stoveWidth: {
    scan: {
      standards: [24 * IN, 30 * IN, 36 * IN, 48 * IN, 60 * IN],
      tolerance: Infinity, // always snap scanned width to the nearest standard
      min: 24 * IN,
      max: 66 * IN,
      fallback: 30 * IN,
    },
    design: { standard: 30 * IN, large: 48 * IN },
  },
  stoveDepth: {
    scan: {
      standards: [25 * IN],
      tolerance: Infinity, // always snap (suggested if ever enabled: 1.5")
      min: 22 * IN,
      max: 32 * IN,
      fallback: 25 * IN,
    },
    design: { standard: 25 * IN },
  },
  stoveHeight: {
    scan: {
      standards: [36 * IN], // matches kitchen-layout convention
      tolerance: Infinity,
      min: 34 * IN,
      max: 40 * IN,
      fallback: 36 * IN,
    },
    design: { standard: 36 * IN },
  },

  // Wall oven. 24" and 27" ovens exist, but 30" dominates and RoomPlan
  // under-reports the visible appliance face (this scan's oven measured
  // 25.5"), so the scan list starts at 30" — the same reasoning that drops
  // 24" from cooktopWidth. A genuinely larger unit still lands on 33"/36".
  ovenWidth: {
    scan: {
      // 24", 27" and 30" are the three widths every wall oven is built in;
      // the list started at 30", so a 26.16" scan had nowhere nearer to go
      // and inflated 3.84" (structure F6D83996). STANDARD_CABINET_WIDTHS
      // already carries the cabinets that house them — a 27" oven takes a
      // 30" cabinet, a 24" oven a 27" one.
      standards: [24 * IN, 27 * IN, 30 * IN, 33 * IN, 36 * IN],
      tolerance: Infinity,
      min: 24 * IN,
      max: 36 * IN,
      fallback: 30 * IN,
    },
    design: { standard: 30 * IN, large: 36 * IN },
  },

  // Built-in wall ovens are ~24" deep so they sit flush in a 24" cabinet;
  // the scanned depth is the door face, which reads slightly proud (25.1"
  // here). One standard, always snapped, mirroring stoveDepth.
  ovenDepth: {
    scan: {
      standards: [24 * IN],
      tolerance: Infinity,
      min: 21 * IN,
      max: 28 * IN,
      fallback: 24 * IN,
    },
    design: { standard: 24 * IN },
  },

  // Drop-in cooktop — a deck set into the countertop, NOT a freestanding
  // range. RoomPlan reports one as a `stove` whose scanned height is only the
  // burner surface it can see (1-3"), so it needs its own dimension targets;
  // running it through stoveHeight would inflate it into a 36" box floating at
  // counter height. See `classifyCooktops` in post-processing/kitchen.js.
  // 24" is deliberately absent from the scan list even though 24" cooktops
  // exist. RoomPlan bounds the visible glass deck, which reads narrower than
  // the unit's nominal width — a real 30" cooktop scanned at 26". Since 24"
  // units are two-burner and uncommon while 30"/36" dominate, a short
  // measurement is far more likely an under-measured 30" than a genuine 24".
  // Trade-off: a scanned two-burner 24" cooktop will widen to 30".
  cooktopWidth: {
    scan: {
      standards: [30 * IN, 36 * IN, 48 * IN],
      tolerance: Infinity,
      min: 24 * IN,
      max: 48 * IN,
      fallback: 30 * IN,
    },
    design: { standard: 30 * IN, large: 36 * IN },
  },
  cooktopDepth: {
    scan: {
      standards: [21 * IN],
      tolerance: Infinity,
      min: 18 * IN,
      max: 24 * IN,
      fallback: 21 * IN,
    },
    design: { standard: 21 * IN },
  },
  // The visible deck only — the burner box below it lives inside the cabinet.
  // 3/4" is the slab a drop-in actually presents above the countertop; 2" read
  // as a chunky block in the 3D view. The cooking surface keeps its relief from
  // the burner heads, which stand proud of the deck (see `cooktopRenderer`) —
  // the deck itself is just thin.
  cooktopHeight: {
    scan: {
      standards: [0.75 * IN],
      tolerance: Infinity,
      min: 0.5 * IN,
      max: 4 * IN,
      fallback: 0.75 * IN,
    },
    design: { standard: 0.75 * IN },
  },

  // Refrigerator width — always snap a scan to the nearest real fridge width.
  fridgeWidth: {
    scan: {
      standards: [
        24 * IN,
        28 * IN,
        30 * IN,
        33 * IN,
        36 * IN,
        42 * IN,
        48 * IN,
      ],
      tolerance: Infinity, // always snap scanned width to the nearest standard
      min: 24 * IN,
      max: 54 * IN,
      fallback: 30 * IN,
    },
    design: { standard: 30 * IN, large: 48 * IN },
  },
  fridgeDepth: {
    scan: {
      standards: [25 * IN, 30 * IN], // counter-depth vs standard
      tolerance: Infinity,
      min: 24 * IN,
      max: 36 * IN,
      fallback: 30 * IN,
    },
    design: { standard: 30 * IN },
  },

  // Dishwasher — depth only (width is not snapped today).
  dishwasherDepth: {
    scan: {
      standards: [24 * IN],
      tolerance: Infinity,
      min: 22 * IN,
      max: 28 * IN,
      fallback: 24 * IN,
    },
    design: { standard: 24 * IN },
  },

  // Kitchen counter (lower-cabinet) depth — snap within 5" of the 24"
  // standard, otherwise honor the scanned depth between 15" and 26".
  //
  // 5" is where the corpus separates, not a round number. Across the 90
  // scanned floor-storage boxes that become base cabinets, the ones inside
  // the honour window fall in two groups with a clean gap between them:
  // 19.2 / 19.6 / 20.1 / 20.4 / 20.6 / 20.9 / 21.1 / 21.4 / 21.9, then
  // nothing until 18.3 / 16.2. The first group is measurement noise on a
  // standard cabinet — RoomPlan reads the door face, and beautify shrinks
  // further — and none of those is a depth anyone builds. The second is a
  // genuinely shallow run (the 15.75" run against a pinched corridor that
  // `runCabinetDepth` exists to preserve). The gap midpoint is 18.75", i.e.
  // a tolerance of 5.25"; 5" sits just inside it and keeps 18.3" honoured
  // with 0.7" to spare. 2" caught only the 22"+ boxes and left every one of
  // that first group standing 3-5" shallow; 6" crosses the gap and starts
  // flattening real shallow runs.
  //
  // Note `min`/`max` already dominate outside 15-26": a 31" or 54" storage
  // box is out of range and takes the 24" fallback today, so the tolerance
  // only ever governs the 15-22" window.
  cabinetDepth: {
    scan: {
      standards: [KITCHEN_CABINET_DIMENSIONS.depth],
      tolerance: 5 * IN,
      min: 15 * IN,
      max: 26 * IN,
      fallback: KITCHEN_CABINET_DIMENSIONS.depth,
    },
    design: { standard: KITCHEN_CABINET_DIMENSIONS.depth },
  },

  // Upper cabinets: scanned "upper" dims are unreliable (see
  // convertToUpperCabinet in post-processing/kitchen.js) → always snap.
  // Honoring is intentionally never enabled here.
  upperCabinetDepth: {
    scan: {
      standards: [UPPER_CABINET_DIMENSIONS.depth],
      tolerance: Infinity,
      min: UPPER_CABINET_DIMENSIONS.depth,
      max: UPPER_CABINET_DIMENSIONS.depth,
      fallback: UPPER_CABINET_DIMENSIONS.depth,
    },
    design: { standard: UPPER_CABINET_DIMENSIONS.depth },
  },
  // A built-in oven UNDER a counter. Single wall ovens are built at these
  // heights; a 34.5" base cabinet standing on a 4" toe kick leaves 30.5", so
  // anything above that cannot be an under-counter oven whatever the scan
  // says (structure 31B79781 measured 32.94"). Deliberately NOT applied by
  // `snapApplianceWidths` — a wall oven in a tall column is a different
  // appliance and is routinely far taller (43.8" for a double).
  ovenHeight: {
    scan: {
      standards: [24 * IN, 27 * IN, 29 * IN],
      tolerance: Infinity,
      min: 20 * IN,
      max: 34 * IN,
      fallback: 29 * IN,
    },
    design: { standard: 29 * IN },
  },
  upperCabinetHeight: {
    scan: {
      standards: [UPPER_CABINET_DIMENSIONS.height],
      tolerance: Infinity,
      min: UPPER_CABINET_DIMENSIONS.height,
      max: UPPER_CABINET_DIMENSIONS.height,
      fallback: UPPER_CABINET_DIMENSIONS.height,
    },
    design: { standard: UPPER_CABINET_DIMENSIONS.height },
  },
};

module.exports = {
  snapOrHonor,
  nearestStandard,
  applyKitchenDimensionPolicy,
  KITCHEN_DIMENSION_POLICY,
};
